import type { ReactNode, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

interface EntityLinkProps {
  to: string;
  children: ReactNode;
  className?: string;
}

/**
 * Internal entity link — SPA navigation via React Router.
 * Calls stopPropagation to prevent row-click handlers from firing.
 */
export function EntityLink({ to, children, className = '' }: EntityLinkProps) {
  return (
    <Link
      to={to}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
      className={`text-accent hover:text-accent-hover hover:underline underline-offset-2 transition-colors ${className}`}
    >
      {children}
    </Link>
  );
}

interface ExternalServiceLinkProps {
  href: string;
  label?: string;
  icon?: ReactNode;
  className?: string;
  // Explicit accessible name. Required for icon-only renders (no
  // visible `label`) — `title` alone is *not* an accessible name
  // for `<a>` per ARIA spec, so screen readers read out the raw
  // `href`. Pass an already-translated string; the caller owns the
  // key, and it is a HOST key — this package's catalog carries no
  // name for any particular external service (ADR-0035 D7).
  ariaLabel?: string;
}

/**
 * External service link — opens in new tab with an icon.
 * Calls stopPropagation to prevent row-click handlers from firing.
 */
export function ExternalServiceLink({
  href,
  label,
  icon,
  className = '',
  ariaLabel,
}: ExternalServiceLinkProps) {
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  // Accessible name resolution priority: explicit `ariaLabel` > visible
  // non-empty `label` (auto-promoted) > nothing. Coercing `label === ""`
  // to `undefined` defends against a footgun: `aria-label=""` is
  // rendered as an *empty* attribute by React, which per ARIA spec
  // suppresses the accessible name and leaves screen readers narrating
  // a bare "link" with no destination cue.
  const accessibleName = ariaLabel ?? (label || undefined);

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={handleClick}
      title={accessibleName ?? href}
      aria-label={accessibleName}
      className={`inline-flex items-center gap-1 text-muted-foreground hover:text-accent transition-colors ${className}`}
    >
      {icon || <ExternalLink className="h-3 w-3" />}
      {label && <span className="text-xs">{label}</span>}
    </a>
  );
}
